import { ICustomBinding } from "#/DefaultViewExtensionInterfaces";
import { ICoreFrameGridBindingParameter } from '#/DefaultViewExtensionInterfaces';
import Utilities from '@/Utility/Utilities';
import { CoreFrameGrid, type ICoreFrameGrid } from 'rad-coreframe-grid';
import ResourceManager from '@/Management/ResourceManager';

export default class CoreFrameGridBinding implements ICustomBinding {

    public initBinding() {
        ko.bindingHandlers.coreFrameGrid = {
            init: (node: HTMLElement, valueAccessor: () => ICoreFrameGridBindingParameter) => {
                let element = $(node);
                let value = valueAccessor();
                let columns = CoreFrameGridBinding.makeFunction(value.columns);
                let data = CoreFrameGridBinding.makeFunction(value.data);

                let height = CoreFrameGridBinding.makeFunction(value.height, undefined);
                let editable = CoreFrameGridBinding.makeFunction(value.editable, false);
                let sortable = CoreFrameGridBinding.makeFunction(value.sortable);
                let sortingClickStrategy = value.sortingClickStrategy;
                let editMode = value.editMode;
                let doubleClickToEdit = value.doubleClickToEdit;
                let applyOnLostFocus = value.applyOnLostFocus;
                let rowIdName = CoreFrameGridBinding.makeFunction(value.rowIdName);
                let onBeforeEdit = value.onBeforeEdit;
                let onSortChange = value.onSortChange;
                let onDblClick = value.onDblClick;
                let onSave = value.onSave;
                let onDelete = value.onDelete;
                let canAdd = CoreFrameGridBinding.makeFunction(value.canAdd, false);
                let onAdd = value.onAdd;
                let onKeyDown = value.onKeyDown;
                let fireEditWithoutClick = value.fireEditWithoutClick;
                let isEnabled = CoreFrameGridBinding.makeFunction(value.enabled, true);
                let paging = CoreFrameGridBinding.makeFunction(value.paging, undefined);
                let summaryRow = CoreFrameGridBinding.makeFunction(value.summaryRow, undefined);
                let selected = CoreFrameGridBinding.makeFunction(value.selected, undefined);
                let showHeader = value.showHeader;
                let locator = CoreFrameGridBinding.makeFunction(value.locator, '');
                let selectionMode = CoreFrameGridBinding.makeFunction(value.selectionMode, undefined);
                let onSelectionChange = value.onSelectionChange;
                let onColumnResized = value.onColumnResized;
                let onMouseOver = value.onMouseOver;
                let onMouseOut = value.onMouseOut;
                let onBeforeRender = value.onBeforeRender;
                let onAfterRender = value.onAfterRender;
                let orderBy = CoreFrameGridBinding.makeFunction(value.orderBy, undefined);
                let onFocusLost = value.onFocusLost;
                let hasTotalRow = CoreFrameGridBinding.makeFunction(value.totalRow?.isEnabled, false);
                let renderGridAsync = value.renderGridAsync;
                let isHeightUserAdjustable = CoreFrameGridBinding.makeFunction(value.isHeightUserAdjustable, false);
                let onUserResize = value.onUserResize;
                let tooltip = value.tooltip;
                let autoHeight = value.autoHeight;
                let elementTemplates = value.elementTemplates;
                let onEditModeChanged = value.onEditModeChanged;
                let onHeightReset = value.onHeightReset;
                let defaultHeight = CoreFrameGridBinding.makeFunction(value.defaultHeight, '100%');

                let subscriptions = [];

                let grid = new CoreFrameGrid({
                    element: element,
                    columns: columns(),
                    data: data(),
                    onSelectionChange: onSelectionChange,
                    selectionMode: selectionMode(),
                    height: height(),
                    onSortChange: onSortChange,
                    onDblClick: onDblClick,
                    onMouseOver: onMouseOver,
                    onMouseOut: onMouseOut,
                    isSortable: sortable(),
                    sortingClickStrategy: sortingClickStrategy,
                    isEditable: editable(),
                    editMode: editMode,
                    canAdd: canAdd(),
                    rowIdName: rowIdName(),
                    onBeforeEdit: onBeforeEdit,
                    doubleClickToEdit: doubleClickToEdit,
                    applyOnLostFocus: applyOnLostFocus,
                    onSave: onSave,
                    onDelete: onDelete,
                    onAdd: onAdd,
                    onKeyDown: onKeyDown,
                    isEnabled: isEnabled(),
                    paging: paging(),
                    summaryRow: summaryRow(),
                    selected: selected(),
                    showHeader: showHeader,
                    locator: locator(),
                    onColumnResized: onColumnResized,
                    onBeforeRender: onBeforeRender,
                    onAfterRender: onAfterRender,
                    onBeforeRowRemoved: (row: JQuery<HTMLElement>) => {
                        if (row.length > 0)
                            ko.cleanNode(row[0]);
                    },
                    orderBy: orderBy(),
                    onFocusLost: onFocusLost,
                    hasTotalRow: hasTotalRow(),
                    renderGridAsync: renderGridAsync,
                    tooltip: tooltip,
                    isHeightUserAdjustable: isHeightUserAdjustable(),
                    onUserResize: onUserResize,
                    elementTemplates: elementTemplates,
                    onEditModeChanged: onEditModeChanged,
                    onHeightReset: onHeightReset,
                    defaultHeight: defaultHeight(),
                    getResource: ResourceManager.getInstance().get
                } as ICoreFrameGrid) as CoreFrameGrid;
                grid.init();
                type SubscriptionCallback<T> = (newValue: T) => void;
                type Subscribable<T> = { (): T, subscribe?: (cb: SubscriptionCallback<T>) => any };
                let redirectSubscription = <T>(subscribable: Subscribable<T> | KnockoutObservable<T>, callback: SubscriptionCallback<T>) => {
                    if (subscribable && Utilities.isFunction(subscribable.subscribe)) {
                        subscriptions.push(subscribable.subscribe(callback));
                    }
                };

                redirectSubscription(data, newData => grid.setData(newData));
                redirectSubscription(selected, newSelected => grid.setSelectedIds(newSelected));
                redirectSubscription(editable, editable => grid.setIsEditable(editable));
                redirectSubscription(sortable, sortable => grid.setIsSortable(sortable));
                redirectSubscription(columns, newColummns => grid.setColumns(newColummns));
                redirectSubscription(height, newHeight => grid.setHeight(newHeight));
                redirectSubscription(isEnabled, isEnabled => grid.setIsEnabled(isEnabled));
                redirectSubscription(canAdd, canAdd => grid.setCanAdd(canAdd));
                redirectSubscription(fireEditWithoutClick, rowId => grid.enterEditModeById(rowId, true));
                redirectSubscription(paging, newPaging => grid.updatePaging(newPaging));
                redirectSubscription(orderBy, newOrderBy => grid.setSortExpression(newOrderBy));
                redirectSubscription(summaryRow, newSummaryRow => grid.setSummaryRow(newSummaryRow));
                redirectSubscription(autoHeight, newAutoHeight => {
                    if (!newAutoHeight)
                        return;

                    grid.updateHeightBasedOnRows(newAutoHeight.rows, newAutoHeight.additionalHeight);
                });

                if (value.totalRow?.refreshTrigger) {
                    redirectSubscription(value.totalRow?.refreshTrigger, () => grid.refreshTotalRow());
                }

                value.widget(grid);

                ko.utils.domNodeDisposal.addDisposeCallback(node, () => {
                    subscriptions.forEach(s => s.dispose());
                    subscriptions = [];
                    grid.dispose();
                });
            }
        };
    }

    private static makeFunction<T>(candidate: ObservableOrDefault<T>, defaultValue?: T): () => T {
        if (Utilities.isFunction(candidate)) {
            return candidate;
        }

        return () => candidate || defaultValue;
    }
}